import type { RpaSegmentedTask } from '../../contracts/projects.types';
import type { WorkflowSkill } from '../../contracts/workflow-skill';
import {
  projectsComposerDraftWireText,
  type ProjectsComposerPromptDraft,
  type ProjectsComposerPromptSegment,
  type ProjectsComposerSkillReference,
} from './projects-composer-editor-draft';

export const MAX_PROJECTS_COMPOSER_SKILL_REFS = 1;

export type CompileSegmentedTaskErrorCode =
  | 'workflow_not_found'
  | 'empty_text_segment'
  | 'too_many_skill_refs';

export class CompileSegmentedTaskError extends Error {
  readonly code: CompileSegmentedTaskErrorCode;

  constructor(code: CompileSegmentedTaskErrorCode, message: string) {
    super(message);
    this.name = 'CompileSegmentedTaskError';
    this.code = code;
  }
}

export type ResolveProjectsComposerSkill = (
  skill: ProjectsComposerSkillReference
) => WorkflowSkill | null | undefined;

export const resolveAvailableWorkflowSkill =
  (skills: readonly WorkflowSkill[]): ResolveProjectsComposerSkill =>
  (skill) => {
    const bySkillId = skill.skillId
      ? skills.find((candidate) => candidate.skillId === skill.skillId)
      : undefined;
    if (bySkillId) return bySkillId;

    return skill.slug
      ? skills.find((candidate) => candidate.slug === skill.slug)
      : undefined;
  };

/**
 * Compile a composer draft to the RPA segmented task. Everything that is not a
 * skill (typed text, objects, starter bundles) accumulates into ONE text
 * run, flushed only at a `skill_ref` and at the end: the backend caps a task at
 * three segments (text / skill_ref / text) and newline-joins free-text
 * segments, so splitting a sentence around an object would both 422 and
 * garble it. The run's text comes from `projectsComposerDraftWireText`, the
 * same join the chat prompt uses, so the task and the prompt cannot disagree.
 */
export const compileSegmentedTask = (
  draft: ProjectsComposerPromptDraft,
  resolveSkill: ResolveProjectsComposerSkill
): RpaSegmentedTask => {
  const segments: RpaSegmentedTask['segments'] = [];
  let skillRefCount = 0;
  const run: ProjectsComposerPromptSegment[] = [];

  const flushRun = () => {
    const text = projectsComposerDraftWireText(run).trim();
    if (text.length > 0) segments.push({ kind: 'text', text });
    run.length = 0;
  };

  for (const segment of draft.segments) {
    if (segment.kind !== 'skill') {
      run.push(segment);
      continue;
    }

    skillRefCount += 1;
    if (skillRefCount > MAX_PROJECTS_COMPOSER_SKILL_REFS) {
      throw new CompileSegmentedTaskError(
        'too_many_skill_refs',
        'A task can include at most one skill.'
      );
    }

    const skill = resolveSkill(segment.skill);
    if (!skill) {
      throw new CompileSegmentedTaskError('workflow_not_found', 'Skill is unavailable');
    }

    flushRun();
    segments.push({
      kind: 'skill_ref',
      skill_id: skill.skillId,
      slug: skill.slug,
    });
  }
  flushRun();

  if (segments.length === 0) {
    throw new CompileSegmentedTaskError('empty_text_segment', 'Task is empty');
  }

  return { segments };
};
